import { useMemo } from 'react';
import { useTypedQuery } from 'apiTyped/helpers/useTypedQuery';
import type {
  Competitor,
  BrandRow,
  RankTile,
  RadarDataPoint,
  EvolutionDataPoint,
  HeroComparisonData
} from 'v2/pages/CompetitorsPositioning.types';

export interface EvolutionSeries {
  key: string;
  color: string;
  label: string;
  isYou?: boolean;
}

export interface CompetitorsPositioningFilters {
  market?: string | null;
  channel?: string | null;
  brand?: string | null;
  period?: string | null;
  competitors?: string[];
  productType?: string | null;
}

export const useCompetitorsPositioning = (
  filters: CompetitorsPositioningFilters = {}
) => {
  const { isLoading, data, refetch } = useTypedQuery(
    'get',
    '/v2/competitors/positioning',
    {
      params: {
        query: {
          market: filters.market ?? undefined,
          channel: filters.channel ?? undefined,
          brand: filters.brand ?? undefined,
          period: filters.period ?? undefined,
          competitors: filters.competitors?.join(',') || undefined,
          product_type: filters.productType ?? undefined
        }
      }
    }
  );

  const mapped = useMemo(() => {
    if (!data) return null;

    const hero: HeroComparisonData = {
      yourBrand: {
        name: data.hero.your_brand.name,
        color: data.hero.your_brand.color,
        rank: data.hero.your_brand.rank,
        totalBrands: data.hero.your_brand.total_brands,
        nd: data.hero.your_brand.nd,
        marketShare: data.hero.your_brand.market_share,
        posNet: data.hero.your_brand.pos_net
      },
      topCompetitor: {
        name: data.hero.top_competitor.name,
        color: data.hero.top_competitor.color,
        rank: data.hero.top_competitor.rank,
        totalBrands: data.hero.top_competitor.total_brands,
        nd: data.hero.top_competitor.nd,
        marketShare: data.hero.top_competitor.market_share,
        posNet: data.hero.top_competitor.pos_net
      },
      metricsWhere: data.hero.metrics_where as { you: number; total: number }
    };

    const competitors: Competitor[] = data.competitors;

    const brandRows: BrandRow[] = data.brand_rows.map((r) => ({
      name: r.name,
      color: r.color,
      rank: r.rank,
      refs: r.refs,
      nd: r.nd,
      evol3m: r.evol_3m,
      evol6m: r.evol_6m,
      evol12m: r.evol_12m,
      avgPrice: r.avg_price,
      visibility: r.visibility,
      penetration: r.penetration,
      loyalty: r.loyalty,
      consumption: r.consumption,
      posGain: r.pos_gain,
      posLost: r.pos_lost,
      isYou: r.is_you ?? undefined,
      isBest: r.is_best ?? undefined
    }));

    const rankTiles: RankTile[] = data.rank_tiles as RankTile[];

    const radarData: RadarDataPoint[] = data.radar_data;

    const evolutionData: EvolutionDataPoint[] = data.evolution_data;

    const evolutionSeries: EvolutionSeries[] = data.evolution_series.map(
      (s) => ({
        key: s.key,
        color: s.color,
        label: s.label,
        isYou: s.is_you ?? undefined
      })
    );

    return {
      hero,
      competitors,
      brandRows,
      rankTiles,
      radarData,
      evolutionData,
      evolutionSeries
    };
  }, [data]);

  return {
    isLoading,
    hero: mapped?.hero ?? null,
    competitors: mapped?.competitors ?? [],
    brandRows: mapped?.brandRows ?? [],
    rankTiles: mapped?.rankTiles ?? [],
    radarData: mapped?.radarData ?? [],
    evolutionData: mapped?.evolutionData ?? [],
    evolutionSeries: mapped?.evolutionSeries ?? [],
    refetch
  };
};
